/**
 * agentAvatar — pure helpers for agent avatar monogram rendering.
 *
 * avatarHue(urn) computes a deterministic palette index from a djb2 hash
 * of the agent URN. The palette has 8 hues spread across the colour wheel
 * so that agents rendered side-by-side are visually distinguishable.
 *
 * Palette (index 0–7):
 *   0 — coral/salmon  oklch(0.74 0.14 25)
 *   1 — amber         oklch(0.76 0.14 65)
 *   2 — lime          oklch(0.72 0.13 135)
 *   3 — teal          oklch(0.68 0.12 185)
 *   4 — sky           oklch(0.70 0.12 240)
 *   5 — indigo        oklch(0.64 0.14 285)
 *   6 — violet        oklch(0.68 0.13 315)
 *   7 — rose          oklch(0.72 0.13 5)
 *
 * Foreground ink for all palette slots is --ink (dark) — the lightness
 * values are chosen so dark text is readable on every background.
 */

const AVATAR_PALETTE = [
  { bg: 'oklch(0.84 0.09 25)',  fg: 'oklch(0.22 0.01 80)' }, // 0 coral
  { bg: 'oklch(0.86 0.1  65)',  fg: 'oklch(0.22 0.01 80)' }, // 1 amber
  { bg: 'oklch(0.84 0.09 135)', fg: 'oklch(0.22 0.01 80)' }, // 2 lime
  { bg: 'oklch(0.82 0.09 185)', fg: 'oklch(0.22 0.01 80)' }, // 3 teal
  { bg: 'oklch(0.82 0.09 240)', fg: 'oklch(0.22 0.01 80)' }, // 4 sky
  { bg: 'oklch(0.78 0.1  285)', fg: 'oklch(0.22 0.01 80)' }, // 5 indigo
  { bg: 'oklch(0.82 0.1  315)', fg: 'oklch(0.22 0.01 80)' }, // 6 violet
  { bg: 'oklch(0.84 0.09 5)',   fg: 'oklch(0.22 0.01 80)' }, // 7 rose
];

/**
 * djb2Hash computes a non-negative 32-bit djb2 hash of a string.
 * The hash is deterministic — same input always yields the same output.
 *
 * @param {string} s
 * @returns {number}
 */
export function djb2Hash(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    // djb2: h = h * 33 + charCode
    // Use bitwise OR 0 to keep it a signed 32-bit integer during the loop,
    // then return h >>> 0 to make it unsigned for the modulo.
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

/**
 * avatarHue returns the palette entry for a given agent URN.
 * The returned object has `bg` and `fg` CSS colour strings.
 *
 * @param {string} urn
 * @returns {{ bg: string, fg: string }}
 */
export function avatarHue(urn) {
  const idx = djb2Hash(urn || '') % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx];
}
